import React from 'react';
import { Table, Button, Space, Tooltip, Typography } from 'antd';
import { EyeOutlined, PlayCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';
import type { SubtaskTableProps, PlanogramSubtask } from '../../../types/plan-sub-task.js';
import { STATUS_ACTIONS } from '../../../types/plan-sub-task.js';
import StatusTag from './StatusTag';
import dayjs from 'dayjs';

const { Text } = Typography;

/**
 * Subtask Table Component
 * Displays subtasks in a data table with actions and status indicators
 */
const SubtaskTable: React.FC<SubtaskTableProps> = ({
  subtasks,
  loading,
  pagination,
  onRowClick,
  onPageChange,
  onStatusUpdate,
  onCompleteTask
}) => {
  const { t } = useTranslation();

  // Handle action button clicks
  const handleAction = (action: string, subtask: PlanogramSubtask, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    
    switch (action) {
      case 'view':
        onRowClick(subtask);
        break;
      case 'start':
        onStatusUpdate(subtask.subtaskCode, 'Ongoing');
        break;
      case 'complete':
        // Navigate to task completion page
        onCompleteTask(subtask);
        break;
      default:
        console.warn('Unknown action:', action);
    }
  };

  // Render action buttons based on status
  const renderActions = (subtask: PlanogramSubtask) => {
    const availableActions = STATUS_ACTIONS[subtask.status];
    
    return (
      <Space size="small">
        {availableActions.includes('view') && (
          <Tooltip title={t('subtask.table.actions.view')}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={(e) => handleAction('view', subtask, e)}
            />
          </Tooltip>
        )}
        {availableActions.includes('start') && (
          <Tooltip title={t('subtask.table.actions.start')}>
            <Button
              type="text"
              size="small"
              icon={<PlayCircleOutlined />}
              style={{ color: '#1890ff' }}
              onClick={(e) => handleAction('start', subtask, e)}
            />
          </Tooltip>
        )}
        {availableActions.includes('complete') && (
          <Tooltip title={t('subtask.table.actions.complete')}>
            <Button
              type="text"
              size="small"
              icon={<CheckCircleOutlined />}
              style={{ color: '#52c41a' }}
              onClick={(e) => handleAction('complete', subtask, e)}
            />
          </Tooltip>
        )}
      </Space>
    );
  };

  // Format date display
  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('YYYY-MM-DD HH:mm');
  };

  const formatDateByDay = (dateString: string) => {
    return dayjs(dateString).format('YYYY-MM-DD');
  };

  // Table columns configuration
  const columns: ColumnsType<PlanogramSubtask> = [
    {
      title: t('subtask.planCode'),
      dataIndex: 'planCode',
      key: 'planCode',
      width: 140,
      fixed: 'left',
    },
    {
      title: t('subtask.table.taskCode'),
      dataIndex: 'subtaskCode',
      key: 'subtaskCode',
      width: 120,
      fixed: 'left',
      render: (code: string) => (
        <Text strong style={{ color: '#1890ff' }}>
          {code}
        </Text>
      )
    },
    {
      title: t('subtask.table.taskName'),
      dataIndex: 'subtaskName',
      key: 'subtaskName',
      width: 200,
      ellipsis: {
        showTitle: false
      },
      render: (name: string) => (
        <Tooltip title={name}>
          <Text>{name}</Text>
        </Tooltip>
      )
    },
    {
      title: t('subtask.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'left',
      render: (status, record) => (
        <StatusTag 
          status={status} 
          overdueFlag={record.overdueFlag}
          size="small"
        />
      ),
      // filters: [
      //   { text: t('subtask.filter.pending'), value: 'Pending' },
      //   { text: t('subtask.filter.active'), value: 'Active' },
      //   { text: t('subtask.filter.ongoing'), value: 'Ongoing' },
      //   { text: t('subtask.filter.finished'), value: 'Finished' },
      //   { text: t('subtask.filter.cancelled'), value: 'Cancelled' },
      //   { text: t('subtask.filter.paused'), value: 'Paused' }
      // ],
      // onFilter: (value, record) => record.status === value,
      // filterOnClose: false
    },
    {
      title: t('subtask.table.milestone'),
      dataIndex: 'milestoneName',
      key: 'milestoneName',
      width: 150,
      ellipsis: {
        showTitle: false
      },
      render: (milestone: string) => (
        <Tooltip title={milestone}>
          <Text>{milestone}</Text>
        </Tooltip>
      )
    },
    {
      title: t('subtask.table.assignee'),
      dataIndex: 'assignee',
      key: 'assignee',
      width: 100,
      render: (assignee: string) => <Text>{assignee}</Text>
    },
    {
      title: t('subtask.table.relatedPlanCode'),
      dataIndex: 'planCode',
      key: 'planCode',
      width: 160,
      ellipsis: {
        showTitle: false
      },
      render: (planCode: string) => (
        <Tooltip title={planCode}>
          <Text style={{ color: '#1890ff' }}>{planCode}</Text>
        </Tooltip>
      )
    },
    {
      title: t('subtask.table.relatedPlanName'),
      dataIndex: 'planName',
      key: 'planName',
      width: 200,
      ellipsis: {
        showTitle: false
      },
      render: (planName: string) => (
        <Tooltip title={planName}>
          <Text>{planName}</Text>
        </Tooltip>
      )
    },
    {
      title: t('common.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      width: 140,
      render: (startDate: string | undefined) => (
        <Text type={startDate ? undefined : 'secondary'}>
          {startDate ? formatDate(startDate) : '-'}
        </Text>
      ),
      // sorter: (a, b) => {
      //   if (!a.startDate && !b.startDate) return 0;
      //   if (!a.startDate) return 1;
      //   if (!b.startDate) return -1;
      //   return dayjs(a.startDate).unix() - dayjs(b.startDate).unix();
      // }
    },
    {
      title: t('subtask.filter.dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 140,
      render: (dueDate: string, record) => {
        const isOverdue = record.overdueFlag;
        return (
          <Text type={isOverdue ? 'danger' : undefined}>
            {formatDateByDay(dueDate)}
          </Text>
        );
      },
      // sorter: (a, b) => dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix(),
    },
    {
      title: t('subtask.detail.completionTime'),
      dataIndex: 'completionDate',
      key: 'completionDate',
      width: 140,
      render: (completionDate: string | undefined, record) => {
        if (!completionDate) {
          return <Text type="secondary">-</Text>;
        }
        
        const isOverdue = record.overdueFlag && record.status === 'Finished';
        return (
          <Text type={isOverdue ? 'danger' : undefined}>
            {formatDate(completionDate)}
          </Text>
        );
      },
      // sorter: (a, b) => {
      //   if (!a.completionDate && !b.completionDate) return 0;
      //   if (!a.completionDate) return 1;
      //   if (!b.completionDate) return -1;
      //   return dayjs(a.completionDate).unix() - dayjs(b.completionDate).unix();
      // }
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 120,
      fixed: 'right',
      align: 'left',
      render: (_, record) => renderActions(record)
    }
  ];

  return (
    <Table<PlanogramSubtask>
      columns={columns}
      dataSource={subtasks}
      rowKey="subtaskCode"
      loading={loading}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => 
          `${t('common.show')} ${range[0]}-${range[1]} ${t('common.of')} ${total} ${t('common.items')}`,
        pageSizeOptions: ['10', '20', '50', '100'],
        onChange: onPageChange,
        onShowSizeChange: onPageChange
      }}
      scroll={{ x: 1560, y: 500 }}
      size="small"
      rowClassName={(record) => {
        if (record.overdueFlag && record.status !== 'Finished') {
          return 'overdue-row';
        }
        return '';
      }}
    />
  );
};

export default SubtaskTable;